import React, { useContext } from 'react';
import SectionBlock from './SectionBlock';
import { MatchingDialogContext } from '../configs';
import { distinguishBetweenMatchingEntries, showSection } from '../utils';
import { commonDisciplines } from '../../../marketplace-custom-config';
import { IconChartL } from '../../../icons';
import { findDisciplineLevel } from '../../../util/disciplines';
import config from '../../../config';

const { listingTypeHorse, listingTypePrivateHorse } = config;

const disciplinesDictionary = commonDisciplines.reduce(
    (acc, discipline) => ({ ...acc, [discipline.key]: discipline.label }),
    {}
);

const SectionLevels = () => {
    const {
        scoreData,
        categorySelected,
        currentPageListing,
        viewerListing,
        horseImage,
        riderProfileImage,
    } = useContext(MatchingDialogContext);

    const levelScore = scoreData?.baseScoreBreakdown?.levelScore;

    if (!levelScore) return null;

    const { shouldBeShown } = showSection({
        categorySelected,
        ...levelScore,
    });

    if (!shouldBeShown) return null;

    const currentPageListingPublicData = currentPageListing?.attributes?.publicData || {};
    const viewerListingPublicData = viewerListing?.attributes?.publicData || {};

    const currentPageListingDiciplines = currentPageListingPublicData?.desiredDisciplines || [];
    const viewerListingDiciplines = viewerListingPublicData.desiredDisciplines || [];

    const currentPageListingLevels = currentPageListingDiciplines.map(disciplineKey => ({
        ...findDisciplineLevel(disciplineKey, currentPageListingPublicData),
        discipline: disciplinesDictionary[disciplineKey],
        image:
            currentPageListingPublicData.type === listingTypeHorse ||
            currentPageListingPublicData.type === listingTypePrivateHorse
                ? horseImage
                : riderProfileImage,
    }));
    const viewerListingLevels = viewerListingDiciplines.map(disciplineKey => ({
        ...findDisciplineLevel(disciplineKey, viewerListingPublicData),
        discipline: disciplinesDictionary[disciplineKey],
        image:
            viewerListingPublicData.type === listingTypeHorse ||
            viewerListingPublicData.type === listingTypePrivateHorse
                ? horseImage
                : riderProfileImage,
    }));

    const entriesDictionary = [...currentPageListingLevels, ...viewerListingLevels].reduce(
        (acc, { discipline, grade: currentLevel, image }) => {
            const currentEntryData = acc[discipline];

            if (currentEntryData) {
                const { level: entryLevel, images: entryImages } = currentEntryData;
                const entryImage = entryImages[entryLevel];
                const matches = entryLevel === currentLevel;
                const maxLevelFromBothListings = Math.max(currentLevel, entryLevel);

                return {
                    ...acc,
                    [discipline]: {
                        discipline,
                        matches,
                        level: currentLevel,
                        maxLevelFromBothListings, // show max level into ProgressBarLevels
                        images: matches
                            ? {
                                  [entryLevel]: [image, entryImage[0]],
                              }
                            : {
                                  [entryLevel]: [entryImage[0]],
                                  [currentLevel]: [image],
                              },
                    },
                };
            }
            return {
                ...acc,
                [discipline]: {
                    discipline,
                    matches: false,
                    level: currentLevel,
                    maxLevelFromBothListings: currentLevel,
                    images: { [currentLevel]: [image] },
                },
            };
        },
        {}
    );

    const { matchingEntries, nonMatchingEntries } = distinguishBetweenMatchingEntries(
        entriesDictionary,
        commonDisciplines
    );

    return (
        <SectionBlock scoreData={scoreData}>
            <SectionBlock.Header>
                <IconChartL />
                <span>Niveau</span>
            </SectionBlock.Header>
            <SectionBlock.ScoreBadge sectionScoreData={levelScore} />
            <section>
                {[...matchingEntries, ...nonMatchingEntries].map(
                    (
                        [entryKey, { matches, maxLevelFromBothListings, discipline, images }],
                        index,
                        arr
                    ) => (
                        <>
                            <SectionBlock.ScoreLineWithLevel
                                key={entryKey}
                                matches={matches}
                                lineLabel={discipline}
                                total={4}
                                level={maxLevelFromBothListings}
                                images={images}
                            />
                            {index < arr.length - 1 && <SectionBlock.Delimiter />}
                        </>
                    )
                )}
            </section>
        </SectionBlock>
    );
};

export default SectionLevels;
